import { tripleSwapPairs, weeklyCalendar } from '../data';

function fmtCost(n: number): string {
  const abs = Math.round(Math.abs(n));
  const sign = n < 0 ? '-' : n > 0 ? '+' : '';
  if (abs >= 1000) return `${sign}$${abs.toLocaleString()}`;
  return `${sign}$${abs}`;
}

function fmtAbs(n: number): string {
  const abs = Math.round(Math.abs(n));
  if (abs >= 1000) return `$${abs.toLocaleString()}`;
  return `$${abs}`;
}

const pairMeta: Record<string, { accent: string; bar: string }> = {
  EURUSD: { accent: 'accent', bar: 'bg-accent-500' },
  GBPJPY: { accent: 'secondary', bar: 'bg-secondary-500' },
  USDTRY: { accent: 'primary', bar: 'bg-primary-500' },
};

export default function TripleSwapSection() {
  return (
    <section className="w-full bg-background-100 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* ── Header ── */}
        <div className="max-w-[680px] mb-12 md:mb-16">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.12] text-foreground-950 tracking-tight mb-4">
            Why Wednesday costs 3× more
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            Forex settles T+2, which means Wednesday trades settle Friday. But the weekend pushes settlement
            to Monday — so Wednesday swaps include Saturday and Sunday. One night, three days of interest.
            For high-swap pairs, this isn't a footnote — it is a recurring cost spike that compounds monthly.
          </p>
        </div>

        {/* ── Weekly Calendar Visual ── */}
        <div className="bg-white rounded-xl border border-background-200 p-4 md:p-8 mb-8">
          <div className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.12em] text-foreground-400 mb-4 md:mb-5">
            Weekly swap multiplier schedule
          </div>

          <div className="grid grid-cols-5 gap-1.5 md:gap-4">
            {weeklyCalendar.map((day) => {
              const isWednesday = day.swapMultiplier === 3;

              return (
                <div
                  key={day.label}
                  className={`relative rounded-lg md:rounded-xl p-2 md:p-5 text-center transition-all duration-200 ${
                    isWednesday
                      ? 'bg-accent-50/60 border md:border-2 border-accent-300 shadow-[0_0_0_1px_rgba(var(--accent-500),0.08)]'
                      : 'bg-background-50 border border-background-100'
                  }`}
                >
                  {isWednesday && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-1.5 md:px-2.5 py-0.5 rounded-full bg-accent-500 text-[8px] md:text-[10px] font-bold text-background-50 whitespace-nowrap">
                      Triple
                    </div>
                  )}

                  <div className="text-[10px] md:text-[12px] font-bold text-foreground-500 mb-1 md:mb-2 tracking-wide">
                    {day.label}
                  </div>

                  <div
                    className={`text-[22px] md:text-[40px] font-[800] leading-none tracking-tight mb-1 md:mb-1.5 ${
                      isWednesday ? 'text-accent-600' : 'text-foreground-400'
                    }`}
                  >
                    {day.swapMultiplier}×
                  </div>

                  <p className="hidden md:block text-[11px] leading-relaxed text-foreground-500">
                    {isWednesday
                      ? 'Wed + Sat + Sun combined — covers the weekend gap in T+2 settlement'
                      : 'Standard overnight financing — 1 day of interest differential'}
                  </p>

                  {isWednesday && (
                    <div className="hidden md:block mt-3 pt-3 border-t border-accent-200">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-accent-500 mb-1">
                        T+2 settlement
                      </div>
                      <div className="text-[11px] text-accent-600 leading-relaxed">
                        Wed trade settles Fri → rolls to Mon. Sat/Sun swap charged Wednesday night.
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Per‑pair triple swap cost table ── */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden mb-8">
          <div className="px-6 md:px-8 py-5 border-b border-background-100">
            <div className="flex items-end justify-between">
              <div>
                <div className="text-[14px] font-[750] text-foreground-950 mb-0.5">
                  What triple swap costs in real dollars
                </div>
                <div className="text-[12px] text-foreground-500">
                  1 lot position · normal day vs Wednesday · costs in account currency (USD)
                </div>
              </div>
            </div>
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-50">
                  <th className="text-left px-6 py-4 text-[11px] font-bold uppercase tracking-[0.1em] text-foreground-400 w-[140px]">
                    Pair
                  </th>
                  <th className="text-center px-4 py-4 text-[11px] font-bold uppercase tracking-[0.1em] text-foreground-400">
                    Direction
                  </th>
                  <th className="text-right px-4 py-4 text-[11px] font-bold uppercase tracking-[0.1em] text-foreground-400">
                    Normal day
                  </th>
                  <th className="text-right px-4 py-4 text-[11px] font-bold uppercase tracking-[0.1em] text-foreground-400">
                    Wednesday (3×)
                  </th>
                  <th className="text-right px-4 py-4 text-[11px] font-bold uppercase tracking-[0.1em] text-foreground-400">
                    Extra cost per Wed
                  </th>
                  <th className="text-right px-6 py-4 text-[11px] font-bold uppercase tracking-[0.1em] text-foreground-400">
                    Monthly extra (4 Weds)
                  </th>
                </tr>
              </thead>
              <tbody>
                {tripleSwapPairs.map((p, idx) => {
                  const meta = pairMeta[p.pair] ?? pairMeta.EURUSD;
                  const extraLong = Math.abs(p.wednesdayLongCost) - Math.abs(p.normalDayLongCost);
                  const extraShort = Math.abs(p.wednesdayShortCost) - Math.abs(p.normalDayShortCost);
                  const monthlyExtraLong = extraLong * 4;
                  const monthlyExtraShort = extraShort * 4;

                  return (
                    <>
                      {/* Long row */}
                      <tr
                        key={`${p.pair}-long`}
                        className={`border-b border-background-50/70 transition-colors hover:bg-background-50/50 ${
                          idx % 2 === 0 ? '' : 'bg-background-50/20'
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2.5">
                            <div className={`w-1 h-8 rounded-full ${meta.bar}`} />
                            <span className="text-[14px] font-[750] text-foreground-950">{p.pair}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            p.normalDayLongCost >= 0
                              ? 'bg-[#F0FDF4] text-[#16A34A] border border-[#BBF7D0]'
                              : 'bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA]'
                          }`}>
                            {p.normalDayLongCost >= 0 ? 'Long earns' : 'Long pays'}
                          </span>
                        </td>
                        <td className={`px-4 py-4 text-right text-[14px] font-bold tabular-nums ${
                          p.normalDayLongCost >= 0 ? 'text-[#16A34A]' : 'text-[#DC2626]'
                        }`}>
                          {fmtCost(p.normalDayLongCost)}
                        </td>
                        <td className={`px-4 py-4 text-right text-[14px] font-bold tabular-nums ${
                          p.wednesdayLongCost >= 0 ? 'text-[#16A34A]' : 'text-[#DC2626]'
                        }`}>
                          {fmtCost(p.wednesdayLongCost)}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <span className="text-[13px] font-bold text-[#DC2626]">
                            +{fmtAbs(extraLong)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className={`text-[14px] font-[800] tabular-nums ${
                            monthlyExtraLong >= 100 ? 'text-[#DC2626]' : 'text-foreground-700'
                          }`}>
                            {fmtAbs(monthlyExtraLong)}
                          </span>
                        </td>
                      </tr>

                      {/* Short row */}
                      <tr
                        key={`${p.pair}-short`}
                        className={`border-b border-background-50/70 transition-colors hover:bg-background-50/50 ${
                          idx % 2 === 0 ? '' : 'bg-background-50/20'
                        }`}
                      >
                        <td className="px-6 py-4" />
                        <td className="px-4 py-4 text-center">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            p.normalDayShortCost >= 0
                              ? 'bg-[#F0FDF4] text-[#16A34A] border border-[#BBF7D0]'
                              : 'bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA]'
                          }`}>
                            {p.normalDayShortCost >= 0 ? 'Short earns' : 'Short pays'}
                          </span>
                        </td>
                        <td className={`px-4 py-4 text-right text-[14px] font-bold tabular-nums ${
                          p.normalDayShortCost >= 0 ? 'text-[#16A34A]' : 'text-[#DC2626]'
                        }`}>
                          {fmtCost(p.normalDayShortCost)}
                        </td>
                        <td className={`px-4 py-4 text-right text-[14px] font-bold tabular-nums ${
                          p.wednesdayShortCost >= 0 ? 'text-[#16A34A]' : 'text-[#DC2626]'
                        }`}>
                          {fmtCost(p.wednesdayShortCost)}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <span className="text-[13px] font-bold text-[#DC2626]">
                            +{fmtAbs(extraShort)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className={`text-[14px] font-[800] tabular-nums ${
                            monthlyExtraShort >= 100 ? 'text-[#DC2626]' : 'text-foreground-700'
                          }`}>
                            {fmtAbs(monthlyExtraShort)}
                          </span>
                        </td>
                      </tr>
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-background-50">
            {tripleSwapPairs.map((p) => {
              const meta = pairMeta[p.pair] ?? pairMeta.EURUSD;
              const extraLong = Math.abs(p.wednesdayLongCost) - Math.abs(p.normalDayLongCost);
              const extraShort = Math.abs(p.wednesdayShortCost) - Math.abs(p.normalDayShortCost);

              return (
                <div key={p.pair} className="p-5">
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className={`w-1 h-6 rounded-full ${meta.bar}`} />
                    <span className="text-[16px] font-[750] text-foreground-950">{p.pair}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wide text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded">
                      {p.label}
                    </span>
                  </div>

                  <div className="space-y-3">
                    {/* Long */}
                    <div className="flex items-center justify-between p-3 rounded-lg bg-[#FEF2F2]">
                      <div>
                        <div className="text-[11px] font-bold text-foreground-500">Long {p.pair}</div>
                        <div className="text-[10px] text-foreground-400">Normal: {fmtCost(p.normalDayLongCost)} &rarr; Wed: {fmtCost(p.wednesdayLongCost)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[13px] font-bold text-[#DC2626]">+{fmtAbs(extraLong)} extra</div>
                        <div className="text-[10px] text-foreground-500">per Wednesday</div>
                      </div>
                    </div>

                    {/* Short */}
                    <div className={`flex items-center justify-between p-3 rounded-lg ${p.normalDayShortCost < 0 ? 'bg-[#FEF2F2]' : 'bg-[#F0FDF4]'}`}>
                      <div>
                        <div className="text-[11px] font-bold text-foreground-500">Short {p.pair}</div>
                        <div className="text-[10px] text-foreground-400">Normal: {fmtCost(p.normalDayShortCost)} &rarr; Wed: {fmtCost(p.wednesdayShortCost)}</div>
                      </div>
                      <div className="text-right">
                        <div className={`text-[13px] font-bold ${p.normalDayShortCost < 0 ? 'text-[#DC2626]' : 'text-[#16A34A]'}`}>
                          {p.normalDayShortCost < 0 ? `+${fmtAbs(extraShort)} extra` : `+${fmtAbs(extraShort)} extra`}
                        </div>
                        <div className="text-[10px] text-foreground-500">per Wednesday</div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-background-100 flex items-center justify-between">
                    <span className="text-[11px] text-foreground-500">Monthly extra (4 wednesdays)</span>
                    <span className="text-[14px] font-[800] tabular-nums text-[#DC2626]">
                      Long: {fmtAbs(extraLong * 4)} · Short: {fmtAbs(extraShort * 4)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Formula + Bottom Insight ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {/* Formula card */}
          <div className="bg-white rounded-xl border border-background-200 p-5 md:p-6">
            <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-foreground-400 mb-3">
              The formula
            </div>
            <div className="text-[18px] md:text-[20px] font-[750] text-foreground-950 leading-relaxed mb-4 tracking-tight">
              Wednesday swap = daily swap rate × pip value × lots × <span className="text-accent-600">3</span>
            </div>
            <div className="space-y-2.5">
              <div className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-accent-100 text-accent-600 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                <p className="text-[13px] leading-relaxed text-foreground-600">
                  Find your pair's daily swap rate in your broker's contract specifications (in pips)
                </p>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-accent-100 text-accent-600 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                <p className="text-[13px] leading-relaxed text-foreground-600">
                  Multiply by pip value and lot size to get the normal daily dollar cost
                </p>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-accent-100 text-accent-600 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                <p className="text-[13px] leading-relaxed text-foreground-600">
                  Multiply by <strong className="text-foreground-900">3</strong> when holding through Wednesday 5 PM ET — that single night charges for Wednesday, Saturday, and Sunday
                </p>
              </div>
            </div>
          </div>

          {/* Insight card */}
          <div className="bg-white rounded-xl border border-background-200 p-5 md:p-6">
            <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-foreground-400 mb-3">
              Why this matters
            </div>
            <p className="text-[13px] leading-relaxed text-foreground-700 mb-4">
              Four Wednesdays per month means <strong className="text-foreground-900">12 extra days of swap</strong> you never explicitly signed up for. On EURUSD long, that is only $19/month — a rounding error. But on USDTRY long, four triple-swap Wednesdays cost an extra{' '}
              <strong className="text-[#DC2626]">{fmtAbs((Math.abs(tripleSwapPairs[2].wednesdayLongCost) - Math.abs(tripleSwapPairs[2].normalDayLongCost)) * 4)}</strong> per month
              — just from the Wednesday multiplier, not from holding longer.
            </p>
            <div className="p-4 rounded-lg bg-accent-50/60 border border-accent-100">
              <p className="text-[12px] leading-relaxed text-accent-700 font-semibold">
                Some brokers apply triple swap on Thursday instead of Wednesday. Always check your broker's specific rollover schedule — the day changes but the 3× math stays the same.
              </p>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="mt-6 text-[12px] text-foreground-500 text-center max-w-[680px] mx-auto">
          Triple swap mechanics are standard across the forex industry because of T+2 settlement conventions.
          Individual broker policies may differ — some apply triple swap on Thursday, and some Islamic/swap-free
          accounts may have alternative fee structures for Wednesday positions.
        </p>
      </div>
    </section>
  );
}